import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};

const DEFAULT_SETTINGS = {
  church_name: 'Coos Bay Cornerstone',
  address: '886 S. 4th Street',
  city_state_zip: 'Coos Bay, Oregon 97420',
  phone: '(541) 751-0025',
  email: 'info@coosbaycornerstone.com',
  sunday_school_time: '9:15 AM',
  sunday_service_time: '10:30 AM',
  mission_statement: 'You must have the same attitude that Christ Jesus had.',
  welcome_text: 'Loving God and Others. Biblical Teaching. Traditional Worship.',
  logo_url: '',
  hero_image_url: '',
  pastor_name: 'Pastor Mark',
  pastor_title: 'Senior Pastor',
  pastor_bio: '',
  pastor_image_url: '',
  footer_text: '© 2025 Coos Bay Cornerstone Nazarene. All rights reserved.',
  // About page content
  about_intro: 'At Coos Bay Cornerstone Nazarene, we believe in the transformative power of love. Our church is built on the foundation of loving God with all our hearts and loving our neighbors as ourselves.',
  about_biblical_teaching: 'We are committed to teaching the Word of God with clarity and conviction. Our sermons and Bible studies are grounded in Scripture, helping our congregation grow in faith and understanding.',
  about_worship: 'We honor the rich traditions of Christian worship while creating a warm and welcoming atmosphere for all who enter our doors. Our services blend reverence with relevance.',
  // Homepage settings
  hero_title: 'Welcome to Coos Bay Cornerstone',
  hero_subtitle: 'Loving God and Others',
  hero_tagline: 'Biblical Teaching • Traditional Worship • Community',
  service_section_title: 'Join Us This Sunday',
  sunday_school_label: 'Sunday School',
  sunday_service_label: 'Sunday Service',
  show_service_times: true,
  show_sunday_school: true,
  show_sunday_service: true,
  show_mission: true,
  mission_section_title: 'Our Mission',
  mission_verse: 'Philippians 2:5',
  mission_description: 'We are a faith community dedicated to loving God and others through Biblical teaching and traditional worship. Everyone is welcome at Coos Bay Cornerstone.',
  show_featured_events: true,
  show_announcements: true,
  show_quick_links: true,
  quicklink1_title: 'Upcoming Events',
  quicklink1_description: 'Join us for special events and weekly activities',
  quicklink1_button: 'View Events',
  quicklink2_title: 'Get Involved',
  quicklink2_description: 'Connect with our community through various ministries',
  quicklink2_button: 'Learn More',
  quicklink3_title: 'Support Our Church',
  quicklink3_description: 'Help those in need through your generous giving',
  quicklink3_button: 'Donate Now',
  // Timezone - default to Pacific Time
  timezone: 'America/Los_Angeles'
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => {
    // Try to load cached settings from sessionStorage for instant render
    const cached = sessionStorage.getItem('siteSettings');
    if (cached) {
      try {
        return { ...DEFAULT_SETTINGS, ...JSON.parse(cached) };
      } catch (e) {
        return DEFAULT_SETTINGS;
      }
    }
    return DEFAULT_SETTINGS;
  });
  const [loading, setLoading] = useState(() => !sessionStorage.getItem('siteSettings'));
  const [lastFetch, setLastFetch] = useState(0);

  const API = process.env.REACT_APP_BACKEND_URL || '';

  const fetchSettings = useCallback(async (forceRefresh = false) => {
    // Skip fetch if we fetched recently (within 30 seconds) unless forced
    const now = Date.now();
    if (!forceRefresh && lastFetch && (now - lastFetch) < 30000) {
      return;
    }

    try {
      const response = await axios.get(`${API}/api/site-settings`);
      const newSettings = { ...DEFAULT_SETTINGS, ...response.data };
      setSettings(newSettings);
      setLastFetch(now);
      // Cache in sessionStorage for instant load on next page
      sessionStorage.setItem('siteSettings', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to load site settings:', error);
      // Keep default settings on error
    } finally {
      setLoading(false);
    }
  }, [API, lastFetch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Function to refresh settings (called after admin saves changes)
  const refreshSettings = () => {
    sessionStorage.removeItem('siteSettings');
    fetchSettings(true);
  };

  // Helper to get full address
  const getFullAddress = () => {
    return `${settings.address}, ${settings.city_state_zip}`;
  };

  // Helper to get Google Maps URL
  const getGoogleMapsUrl = () => {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(getFullAddress())}`;
  };

  // Helper to format date/time in the site's timezone
  const formatDateTime = (dateString, options = {}) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: settings.timezone || 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return date.toLocaleString('en-US', { ...defaultOptions, ...options });
    } catch (e) {
      return dateString;
    }
  };

  // Helper to format date only in the site's timezone
  const formatDate = (dateString, options = {}) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: settings.timezone || 'America/Los_Angeles',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      };
      return date.toLocaleDateString('en-US', { ...defaultOptions, ...options });
    } catch (e) {
      return dateString;
    }
  };

  // Helper to format time only in the site's timezone
  const formatTime = (dateString, options = {}) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const defaultOptions = {
        timeZone: settings.timezone || 'America/Los_Angeles',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      return date.toLocaleTimeString('en-US', { ...defaultOptions, ...options });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <SiteSettingsContext.Provider value={{ 
      settings, 
      loading, 
      refreshSettings,
      getFullAddress,
      getGoogleMapsUrl,
      formatDateTime,
      formatDate,
      formatTime
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export default SiteSettingsContext;
